import i18n from 'src/locales/i18n';
import { isUnitedStatesCountry } from 'src/utils/ein';

export const EIN_PROBLEM_STATUSES = [
  'missing',
  'invalid_format',
  'not_found',
  'name_mismatch',
  'revoked',
];

const EIN_STATUS_CHIP_COLOR = {
  green: '#10B981',
  red: '#EF4444',
  amber: '#F59E0B',
  blue: '#3B82F6',
  gray: '#6B7280',
};

// Labels are getters so they translate at read time, not at module load.
const statusMeta = (status, color) => ({
  get label() {
    return i18n.t(`ein-status.labels.${status.replace(/_/g, '-')}`);
  },
  color,
});

export const EIN_STATUS_META = {
  verified: statusMeta('verified', EIN_STATUS_CHIP_COLOR.green),
  missing: statusMeta('missing', EIN_STATUS_CHIP_COLOR.red),
  invalid_format: statusMeta('invalid_format', EIN_STATUS_CHIP_COLOR.red),
  not_found: statusMeta('not_found', EIN_STATUS_CHIP_COLOR.amber),
  name_mismatch: statusMeta('name_mismatch', EIN_STATUS_CHIP_COLOR.amber),
  revoked: statusMeta('revoked', EIN_STATUS_CHIP_COLOR.red),
  unverified: statusMeta('unverified', EIN_STATUS_CHIP_COLOR.gray),
  not_applicable: statusMeta('not_applicable', EIN_STATUS_CHIP_COLOR.gray),
  error: statusMeta('error', EIN_STATUS_CHIP_COLOR.gray),
};

export const EIN_STATUS_OPTIONS = Object.entries(EIN_STATUS_META).map(
  ([value, meta]) => ({
    value,
    get label() {
      return meta.label;
    },
  })
);

export const einStatusLabel = (status) =>
  EIN_STATUS_META[status]?.label || status || '—';

// An EIN a Super Admin verified by hand (ein_override_at). Users see it as
// verified by Careit; "override" is only the admin action's name.
// Label/tooltip are getters so they translate at read time, not at module load.
export const EIN_CAREIT_VERIFIED_META = {
  color: EIN_STATUS_CHIP_COLOR.blue,
  get label() {
    return i18n.t('ein-status.careit-verified');
  },
  get tooltip() {
    return i18n.t('ein-status.careit-verified-tooltip');
  },
};

export const einStatusChipSx = (color, { compact = true } = {}) => ({
  borderRadius: '4px',
  height: compact ? 22 : 28,
  fontSize: compact ? '0.75rem' : '0.8125rem',
  fontWeight: 600,
  bgcolor: `${color}18`,
  color,
  border: `1px solid ${color}40`,
  '& .MuiChip-label': { px: compact ? 1 : 1.25 },
});

// Title/body are getters so they translate at read time, not at module load.
const problemCopy = (prefix, status, extra = {}) => ({
  get title() {
    return i18n.t(`${prefix}.${status.replace(/_/g, '-')}.title`);
  },
  get body() {
    return i18n.t(`${prefix}.${status.replace(/_/g, '-')}.body`);
  },
  ...extra,
});

const COPY = {
  missing: problemCopy('ein-status.problem', 'missing', {
    selfServiceable: true,
  }),
  invalid_format: problemCopy('ein-status.problem', 'invalid_format', {
    selfServiceable: true,
  }),
  not_found: problemCopy('ein-status.problem', 'not_found', {
    selfServiceable: true,
  }),
  name_mismatch: problemCopy('ein-status.problem', 'name_mismatch', {
    selfServiceable: false,
  }),
  revoked: problemCopy('ein-status.problem', 'revoked', {
    selfServiceable: false,
  }),
};

export const getEinProblemCopy = (status) => COPY[status] || null;

const ADMIN_COPY = {
  missing: problemCopy('ein-status.admin-problem', 'missing'),
  invalid_format: problemCopy('ein-status.admin-problem', 'invalid_format'),
  not_found: problemCopy('ein-status.admin-problem', 'not_found'),
  name_mismatch: problemCopy('ein-status.admin-problem', 'name_mismatch'),
  revoked: problemCopy('ein-status.admin-problem', 'revoked'),
};

export const getEinAdminProblemCopy = (status) => ADMIN_COPY[status] || null;

export const shouldRemindAboutEin = (user) => {
  if (!user?.is_non_profit) return false;
  const isDriverOnly = !!user.is_driver && !user.is_admin;
  if (isDriverOnly) return false;
  return EIN_PROBLEM_STATUSES.includes(user.ein_status);
};

export const isEinProblemForOrg = (org) => {
  if (!org?.is_non_profit) return false;
  if (org.ein_override_at) return false;
  return EIN_PROBLEM_STATUSES.includes(org.ein_status);
};

export const isEinBulkSelectable = (row) =>
  !!row && row.ein_status !== 'verified' && !row.ein_override_at;

// Tax receipts are fail-closed: verified IRS match or an admin override.
export const isEinQualifiedForTaxReceipt = (row) => {
  if (!row) return false;
  if (row.ein_qualified === true || row.ein_qualified === 1) return true;
  if (row.ein_qualified === false || row.ein_qualified === 0) return false;
  if (row.ein_override_at || row.recipient_ein_override_at) return true;
  const status =
    row.ein_effective_status ||
    row.recipient_ein_effective_status ||
    row.ein_status ||
    row.recipient_ein_status;
  return status === 'verified';
};

export const TAX_EXEMPT_STATUS = {
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified',
};

// What a donor-facing screen says about a recipient's tax-exempt standing.
// null means "say nothing": the org isn't a US recipient (the EIN check is
// US-only), or the row doesn't carry any EIN data so we can't claim either
// way. Donations to an unverified recipient are still allowed; they just may
// not be a tax write-off, which is what the unverified state tells the donor.
export const getTaxExemptStatus = (org) => {
  if (!org) return null;
  if (org.is_non_profit != null && !org.is_non_profit) return null;
  if (org.country && !isUnitedStatesCountry(org.country)) return null;

  const status =
    org.ein_effective_status ||
    org.recipient_ein_effective_status ||
    org.ein_status ||
    org.recipient_ein_status;
  if (status === 'not_applicable') return null;

  const hasEinData =
    !!status ||
    !!org.ein_override_at ||
    !!org.recipient_ein_override_at ||
    org.ein_qualified != null;
  if (!hasEinData) return null;

  return isEinQualifiedForTaxReceipt(org)
    ? TAX_EXEMPT_STATUS.VERIFIED
    : TAX_EXEMPT_STATUS.UNVERIFIED;
};

// Label/tooltip are getters so they translate at read time, not at module load.
export const TAX_EXEMPT_META = {
  [TAX_EXEMPT_STATUS.VERIFIED]: {
    color: EIN_STATUS_CHIP_COLOR.green,
    get label() {
      return i18n.t('tax-exempt.verified');
    },
    get tooltip() {
      return i18n.t('tax-exempt.verified-tooltip');
    },
  },
  [TAX_EXEMPT_STATUS.UNVERIFIED]: {
    color: EIN_STATUS_CHIP_COLOR.amber,
    get label() {
      return i18n.t('tax-exempt.unverified');
    },
    get tooltip() {
      return i18n.t('tax-exempt.unverified-tooltip');
    },
  },
};

export const taxExemptLabel = (org) => {
  const status = getTaxExemptStatus(org);
  return status ? TAX_EXEMPT_META[status].label : '';
};

// EIN is a US-only identifier, so only US nonprofits are gated on it.
export const isBlockedByRecipientEin = (user) =>
  !!user?.is_non_profit &&
  isUnitedStatesCountry(user.country) &&
  !isEinQualifiedForTaxReceipt(user);

// Admin-granted (artificial) plans and orgs with no plan can be changed
// here. Stripe-backed plans are skipped by the bulk-plan API.
export const isEinPlanChangeable = (row) =>
  !!row && (!row.plan || Number(row.plan_artificial) === 1);

const STATUS_FILTER_FIELD = 'ein_status';
const OVERRIDE_FILTER_FIELD = 'is_overridden';
const STATUS_CHIP_FILTER_ID = 'ein-status-chip';
const OVERRIDE_CHIP_FILTER_ID = 'ein-override-chip';
const CHIP_FILTER_FIELDS = [STATUS_FILTER_FIELD, OVERRIDE_FILTER_FIELD];

const filterField = (item) => item?.field || item?.columnField;
const filterOperator = (item) => item?.operator || item?.operatorValue;

const isOverrideFilterValue = (value) =>
  value === true || value === 1 || value === '1' || value === 'true';

const withoutChipFilters = (filterModel) => ({
  ...(filterModel || {}),
  items: (filterModel?.items || []).filter(
    (item) => !CHIP_FILTER_FIELDS.includes(filterField(item))
  ),
  linkOperator: filterModel?.linkOperator || 'and',
});

// "Problems" and "All" both mean "no status filter", so the filter model alone
// can't tell them apart; the page passes `showAll` to pick between them.
export const getSelectedEinStatusChip = (
  filterModel,
  { showAll = false } = {}
) => {
  const items = filterModel?.items || [];
  const overrideItem = items.find(
    (item) => filterField(item) === OVERRIDE_FILTER_FIELD
  );
  if (overrideItem && isOverrideFilterValue(overrideItem.value)) {
    return 'overrides';
  }
  const statusItem = items.find(
    (item) => filterField(item) === STATUS_FILTER_FIELD
  );
  if (!statusItem) return showAll ? 'all' : 'problems';
  const operator = filterOperator(statusItem);
  if (
    (operator === 'is' || operator === 'equals') &&
    typeof statusItem.value === 'string'
  ) {
    return statusItem.value;
  }
  return null;
};

export const applyEinStatusChip = (filterModel, chipKey) => {
  const selected = getSelectedEinStatusChip(filterModel);
  const next = withoutChipFilters(filterModel);
  if (chipKey === 'problems' || chipKey === 'all' || chipKey === selected) {
    return next;
  }
  if (chipKey === 'overrides') {
    return {
      ...next,
      items: [
        ...next.items,
        {
          id: OVERRIDE_CHIP_FILTER_ID,
          field: OVERRIDE_FILTER_FIELD,
          operator: 'is',
          value: true,
        },
      ],
    };
  }
  return {
    ...next,
    items: [
      ...next.items,
      {
        id: STATUS_CHIP_FILTER_ID,
        field: STATUS_FILTER_FIELD,
        operator: 'is',
        value: chipKey,
      },
    ],
  };
};
